import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'
import { validate } from '@/utils/helpers/Validate'
import { notify } from '@/utils/helpers/Notify'

export const systemViewMixins = {
  data () {
    return {
      initialForm: null,
      formInitState: null,
      form: null,
      endpoint: null,
      moduleName: null,
      payload: null,
      currentPage: 1,
      errMsg: null,
      newModel: null,
      createdNotif: null
    }
  },
  methods: {
    /**
     * Highlights a newly created model and switches to the page at which it is
     * @param id
     */
    highlightNewModel (id) {
      this.newModel = id
      let modelIndex = this.data.findIndex(model => model.id === id)
      this.currentPage = ~~((modelIndex + 1) / 20) + 1
      setTimeout(() => (this.newModel = null), 15000)
    },
    /**
     * Used by Element UI table component. Sets a custom class on a specified row if certain conditions are met.
     * In this case, if row is a newly created model, applies the 'new-row' class to that row.
     * @param row
     * @param index
     * @return {string}
     */
    tableRowClassName (row, index) {
      if (row && row.id === this.newModel) {
        return 'new-row'
      }
      return ''
    },
    /**
     * Resets editMode data, resets form to initial state, and displays the
     * modal to create a new model
     * @param self        Component instance
     */
    createNewModel () {
      const resetNestedValueFields = () => {
        for (let field in this.form) {
          if (typeof this.form[field] === 'object' &&
            this.form[field].hasOwnProperty('label') &&
            this.form[field].hasOwnProperty('value')) {
            this.form[field] = Object.assign({}, { label: '', value: '' })
          }
        }
      }
      this.editMode.active = false
      this.editMode.modelId = null
      this.form = Object.assign({}, this.formInitState)
      resetNestedValueFields()
      this.initialForm = JSON.stringify(this.form)
      if (this.handleDialogVisible && typeof this.handleDialogVisible === 'function') {
        this.handleDialogVisible(true)
      } else {
        this.dialogFormVisible = true
      }
    },
    /**
     * Handles async pagination
     * @param query
     * @param getDeps
     */
    async changePage (query, getDeps = false) {
      this.changingPage = true
      const filters = this.$store.getters.getFilters
      if (filters && Object.keys(filters).length) {
        query = Object.assign({}, filters, query)
      }
      let newPayload = Object.assign({}, this.payload, { query })
      try {
        await this.$store.dispatch('clearLocalState', { clearMutation: this.payload.clearMutation, clearCache: true })
        const resp = await this.$store.dispatch('fetchWithQuery', newPayload)
        if (resp && resp.entities && getDeps) {
          const promises = await this.$store.dispatch('findDependencies', resp.entities)
          await Promise.all(promises)
        }
        await new Promise((resolve, reject) => {
          setTimeout(() => {
            this.changingPage = false
            resolve()
          })
        })
        if (this.selection && this.selection.length && typeof this.restoreSelection === 'function') {
          this.restoreSelection()
        }
        return resp
      } catch (e) {
        this.changingPage = false
        this.$notify({ title: 'Error', message: `Unable to fetch next page`, type: 'error', offset: 0 })
        console.log(e)
      }
    },
    /**
     * Closes the form modal
     */
    closeFormModal () {
      this.dialogFormVisible = false
      this.$store.dispatch('isEditingForm', false)
    },
    /**
     * Tracks whether the form has been changed
     */
    trackFormChange () {
      this.$store.dispatch('isEditingForm', this.hasBeenChanged)
      if (typeof this.displayAmountOrPercentageFields === 'function') {
        this.displayAmountOrPercentageFields()
      }
    },
    /**
     * Generates the form object for a given resource, based on data returned by the OPTIONS call to that resource.
     * If the field has a default value (defaultVal) property set, sets the form field's value as that of the field's
     * default value.
     */
    generateForm () {
      let form = {}
      if (this.fields && this.fields.length) {
        this.fields.forEach(field => {
          if (['select', 'autocomplete'].includes(field.fieldType)) {
            form[field.name] = { label: '', value: '' }
          } else if (field.name === 'active' && field.fieldType === 'checkbox') {
            form[field.name] = true
          } else {
            form[field.name] = ''
          }
          if (field.hasOwnProperty('defaultVal')) {
            form[field.name] = field.defaultVal
          }
        })
      }
      this.form = Object.assign({}, form)
      this.formInitState = Object.assign({}, form)
    },
    /**
     * Formats form data to proper REST configuration prior to being POSTED or PATCHED
     * @param form
     */
    formatPostOrPatchRequest (form) {
      let data = { ...form }
      let requirements = {}
      this.fields.forEach(field => {
        const { type, required } = field
        requirements[field.name] = { type, required }
      })
      for (let field in data) {
        if (!requirements[field]) {
          continue
        }
        if (['integer', 'decimal'].includes(requirements[field].type) && data[field] !== 0 && !data[field]) { // if field is a number and is empty or 0, set it to null
          data[field] = null
        } else if (requirements[field].type === 'string' && !data[field]) { // if field is a string and is empty, set to empty string
          data[field] = ''
        } else if (requirements[field].type === 'boolean' && !data[field]) { // if field is a bool, isn't required, and is empty, set it to false
          data[field] = false
        }
      }
      return data
    },
    handleNestedValueFields (form) {
      for (let field in form) {
        if (this.form[field] && typeof this.form[field] === 'object' && this.form[field].hasOwnProperty('value')) {
          form[field] = this.form[field].value
        }
      }
      return form
    },
    /*
    * Validates and submits form data for basic system models (not data-entry models like JEs, APs, PCs, and POs)
    * @param VueComponent self
    * @param String formattedEntityName
    */
    async submitForm () {
      if (this.hiddenFields && this.hiddenFields.length) {    // Removes hidden fields. Markups view uses this
        this.hiddenFields.forEach(field => {
          if (this.form.hasOwnProperty(field)) delete this.form[field]
        })
      }
      let form = { ...this.form }
      let validationErrors = validate(form, this.fields)
      if (!validationErrors.messages) {
        try {
          form = this.handleNestedValueFields(form)
          form = this.formatPostOrPatchRequest(form)
          let newPayload = Object.assign({}, this.payload, { id: this.editMode.modelId, data: form })
          const resp = await this.$store.dispatch(this.editMode.active ? 'update' : 'save', newPayload)
          if (resp && resp.id) {
            this.$store.dispatch('isEditingForm', false)
            if (this.handleDialogVisible && typeof this.handleDialogVisible === 'function') {
              this.handleDialogVisible(false)
            } else {
              this.dialogFormVisible = false
            }
            this.highlightNewModel(resp.id)
            this.createdNotif = this.$notify({
              title: 'Saved',
              message: `${resp.name ? resp.name : resp.code} saved. Click to view.`,
              type: 'success',
              onClick: () => {
                this.editExistingModel(resp.id)
                this.createdNotif.close()
                this.createdNotif = null
              }
            })
          } else {
            this.$notify({ title: 'Error', message: 'Error saving - please try again', type: 'error', offset: 0 })
          }
        } catch (e) {
          console.log(e)
          this.$notify({ title: 'Error', message: e.msg || 'Error saving - please try again', type: 'error', offset: 0 })
        }
      } else {
        let offset = 0                                                                                  // Set offset so that notifs don't overlap each other
        validationErrors.messages.map(v => {                                                            // Render validation error notifs
          this.$notify({ title: 'Warning', message: v, type: 'warning', offset })
          offset += 100
        })
      }
    },
    /**
     * Loads existing model values into form and displays create/edit modal
     * @param modelId   The id of the model to edit/view
     */
    editExistingModel (modelId) {
      this.editMode.active = true
      this.editMode.modelId = modelId
      const model = this.getById(modelId)
      for (let p in model) {
        if (!model.hasOwnProperty(p) || !Object.keys(this.form).includes(p)) {
          continue
        }
        const field = this.fields.find(f => f.name === p)
        if (field && ['field', 'choice'].includes(field.type) && field.choices && field.choices.length) {
          const choice = field.choices.find(choice => String(choice.value) === String(model[p]))
          this.form[p] = {
            label: (model[p] || model[p] === null) && choice ? choice.display_name : '',
            value: (model[p] || model[p] === null) && choice ? choice.value : ''
          }
        } else if (field && field.type === 'field' && !(field.choices && field.choices.length)) {
          const fieldName = (field.name === 'account_group') ? 'accountgroup' : field.name
          this.form[p] = {
            label: model[p] ? this[`get${capitalizeFirstLetter(fieldName)}ById`](model[p]).code : '',
            value: model[p] ? this[`get${capitalizeFirstLetter(fieldName)}ById`](model[p]).id : ''
          }
        } else {
          this.form[p] = model[p]
        }
      }
      this.initialForm = JSON.stringify(this.form)
      this.dialogFormVisible = true
    },
    /**
     * main getter
     */
    getById (id) {
      return this.$store.getters[`get${capitalizeFirstLetter(this.moduleName)}ById`](id)
    },
    /**
     * Handles transaction selection
     * @param selection   Selected models
     */
    handleSelectionChange (selection) {
      let notThisPage = this.selection.filter(v => v.page !== this.currentPage)
      this.selection = [...notThisPage, ...selection]
    },
    /**
     * Handles selection
     * @param selection Current page selection
     */
    handleTableSelectionChange (selection) {
      const notThisPage = this.selection.filter(modelInSelection => !this.pageData.find(modelInPage => modelInPage.id === modelInSelection.id))
      this.selection = [...notThisPage, ...selection]
    },
    /**
     * Preserves the selected transactions and shows them as selected whenever the table is re-rendered
     * due to a page or tab change
     */
    restoreSelection () {
      let table = this.$refs[`${this.endpoint}Table`]
      if (table) {
        table.$nextTick(() => {
          const thisPageSelectedIds = this.selection
            .filter(v => v.page === this.currentPage)
            .map(v => table.data.find(x => x.id === v.id))
          thisPageSelectedIds.forEach(v => table.toggleRowSelection(v, true))
        })
      }
    },
    /**
     * Preserves the selected transactions and shows them as selected whenever the table is re-rendered
     * due to a page or tab change
     */
    restoreTableSelection () {
      let table = this.$refs[`${this.endpoint}Table`]
      if (table) {
        table.$nextTick(() => {
          const selectedIds = this.selection.map(model => model.id)
          this.pageData.forEach(model => {
            if (selectedIds.includes(model.id)) {
              table.toggleRowSelection(model, true)
            }
          })
        })
      }
    },
    /**
     * Handles what happens when the user clicks on one of the "With selected..." dropdown options
     */
    handleBatchUpdateSelection () {
      switch (this.batchUpdateSelection) {
        case 0:
          this.batchUpdate('delete')
          break
      }
      this.batchUpdateSelection = null
    },
    /**
     * Performs updates on multiple models at once
     * @param operation
     * @param data
     */
    async batchUpdate (operation, data = null) {
      const { selection, payload } = this
      let promises = []
      let preventUpdate = false
      for (let i = 0; i < selection.length; i++) {
        const { id } = selection[i]
        let newPayload
        if (operation === 'update') {
          newPayload = Object.assign({}, payload, { id, data })
          promises.push(this.$store.dispatch('update', newPayload))
        } else if (operation === 'delete') {
          // Do not allow delete.  Send a notif instead.
          preventUpdate = true
          /*
          newPayload = Object.assign({}, payload, { id })
          promises.push(this.$store.dispatch('delete', newPayload))
          */
        }
      }

      if (preventUpdate === true) {
        notify(this, 'warning', 'Sorry, you cannot delete this entry because other data may depend on this.  Please contact support for assistance.')
      } else {
        try {
          await Promise.all(promises)
          if (this.selection && this.selection.length) {
            this.selection = []
          }
        } catch (e) {
          console.log(e)
        }
      }
    },
    /**
     * Filters out all data that does not meet the current filter criteria.
     * @param data    The data to be filtered
     */
    meetsFilterCriteria (data) {
      return data.filter(model => {
        const numOfFilters = this.activeFilters ? Object.keys(this.activeFilters).length : 0
        if (numOfFilters < 1) { // return model if no filters have been applied
          return true
        }
        let requirements = []
        for (let filter in this.activeFilters) {
          if (filter === 'search') {
            const searchString = `${model.code} ${model.name} ${model.description}`.trim().toLowerCase()
            requirements.push(searchString.includes(this.activeFilters.search.toLowerCase()))
            continue
          }
          requirements.push(model.hasOwnProperty(filter) && model[filter] === this.activeFilters[filter])
        }
        return requirements.length === numOfFilters && requirements.every(criteria => !!criteria)
      })
    },
    /**
     * Returns sorted data
     * @return {*}
     */
    sortData (data) {
      if (data && Array.isArray(data) && data.length && data.every(model => model.hasOwnProperty('code'))) {
        const longest = data.map(d => d.code.replace(/-|\s/g, '')).reduce((a, b) => a.length > b.length ? a : b)
        return data.sort((a, b) => {
          let aCode = a.code.replace(/-|\s/g, '')
          let bCode = b.code.replace(/-|\s/g, '')
          if (aCode.length < longest.length) {
            aCode = aCode + '0'.repeat(longest.length - aCode.length)
          }
          if (bCode.length < longest.length) {
            bCode = bCode + '0'.repeat(longest.length - bCode.length)
          }
          return aCode.localeCompare(bCode, undefined, { numeric: true, sensitivity: 'base', ignorePunctuation: true })
        })
      } else if (data.every(model => model.hasOwnProperty('name'))) {
        return data.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base', ignorePunctuation: true }))
      }
      return data
    },

    // Function to setup a dropzone instance to upload files
    setupImportDropzoneOptions (payload) {
      let instance = this.$store.getters.getActiveInstance
      let instanceUrl = (instance.url.endsWith('/') ? instance.url.slice(0, -1) : instance.url)
      let path = this.$route.path.includes('pettycash') ? `/pcs/${this.$route.params.id}` : this.$route.path
      console.log('instanceUrl', instanceUrl)
      return {
        instanceUrl: instanceUrl,
        url: instanceUrl + path,
        headers: '',
        signingUrl: instanceUrl + path,
        signingHeaders: { 'Authorization': 'Bearer ' + instance.token },
        acceptedFiles: '.txt,.pdf,.jpg,.png,.tif,.tiff,.doc,.docx,.xls,.xlsx',
        autoProcessQueue: false,
        maxFileSize: 8,
        uploadMultiple: false,
        parallelUploads: 1,
        fileImportUrl: instanceUrl + '/' + payload.importEndpoint
        // maxFiles: 5,
        // thumbnailHeight: 20,
        // thumbnailWidth: 20
      }
    }
  },
  computed: {
    /**
     * Determines whether the form has been changed in any way
     * @return {boolean}
     */
    hasBeenChanged () {
      if (this.modalName) {
        return (this.visible && this.initialForm) ? (!this.inDataEntryView && JSON.stringify(this.form) !== this.initialForm) : false
      } else {
        return (this.dialogFormVisible && this.initialForm) ? (JSON.stringify(this.form) !== this.initialForm) : false
      }
    },
    /**
     * Returns a formatted list of fields generated from an OPTIONS call to a specified endpoint that can be
     * used to construct a form and perform validation. If a 'hiddenFields' data property is present in the component
     * instance and said property is an array of valid field names, it will iterate over hiddenFields and delete the
     * corresponding field from the fields object obtained form the OPTIONS call.
     * @return {Array}
     */
    fields: {
      get () {
        let fields = Object.assign({}, this.$store.getters.getResourceFieldsByEndpoint(this.endpoint))
        if (this.hiddenFields && Array.isArray(this.hiddenFields)) {
          this.hiddenFields.forEach(field => {
            if (fields[field]) delete fields[field]
          })
        }
        return (fields && typeof fields === 'object') ? Object.values(fields) : []
      },
      set ({ field, property, newValue }) {
        if (field && property) {
          const index = this.fields.findIndex(f => f.name === field)
          if (index > -1) {
            this.fields[index][property] = newValue
          }
        }
      }
    },
    /**
     * Returns an object whose properties are the fields that contain choices, the values of which are key-value pairs
     * of each choice's id and display name.
     * @return {{}}
     */
    fieldChoices () {
      const fieldsWithChoices = this.fields.filter(field => field.choices)
      let indexed = {}
      if (fieldsWithChoices.length) {
        fieldsWithChoices.forEach(field => {
          indexed[field.name] = {}
          field.choices.forEach(choice => {
            indexed[field.name][choice.value] = choice.display_name
          })
        })
      }
      return indexed
    },
    /**
     * Returns the metadata for the data type in the module name
     * @return {*}
     */
    meta () {
      return this.$store.getters[`get${capitalizeFirstLetter(this.moduleName)}Metadata`]
    },
    /**
     * Returns a boolean indicating whether data for this module name is being received
     * @return {*}
     */
    receiving () {
      return this.$store.getters[`get${capitalizeFirstLetter(this.moduleName)}FetchStatus`]
    },
    /**
     * Returns a boolean indicating whether data for this module name is being saved
     * @return {*}
     */
    saving () {
      return this.$store.getters[`get${capitalizeFirstLetter(this.moduleName)}SaveStatus`]
    },
    /**
     * Returns a boolean indicating whether data for this module name is being updated
     * @return {*}
     */
    updating () {
      return this.$store.getters[`get${capitalizeFirstLetter(this.moduleName)}UpdateStatus`]
    },
    /**
     * Returns a boolean indicating whether data for this module name is being deleted
     * @return {*}
     */
    deleting () {
      return this.$store.getters[`get${capitalizeFirstLetter(this.moduleName)}DeleteStatus`]
    },
    /**
     * Returns a boolean indicating whether the user is navigating to a route
     * @return {*}
     */
    navigating () {
      return this.$store.getters.getNavigationStatus
    },
    /**
     * Returns a boolean indicating whether the initial data prefetch has completed
     * @return {*}
     */
    dataLoaded () {
      return this.$store.getters.getInitDataLoadedStatus
    }
  },
  watch: {
    /**
     * Tracks whether the form is has been changed. If so, sets a store property that warns the user
     * that they will lose changes if they try to refresh the page.
     */
    hasBeenChanged () {
      this.$store.dispatch('isEditingForm', !!this.hasBeenChanged)
    },
    /**
     * Tracks whether the form is visible. If not, resets the form component data properties to their original state
     */
    dialogFormVisible () {
      if (!this.dialogFormVisible && this.$refs.form && typeof this.$refs.form.resetComponent === 'function') {
        this.$refs.form.resetComponent()
        this.errMsg = null
      }
    }
  },
  /**
   * Pre-route leave logic.
   * @param to
   * @param from
   * @param next
   */
  beforeRouteLeave (to, from, next) {
    if (this.$store.getters.getIsUserEditingForm && !this.$store.getters.getRouteLeaveConfirmation && !this.inDataEntryView) {
      return this.$store.dispatch('setDestinationRoute', to)
        .then(() => this.$store.dispatch('changeModalVisibility', { entityType: 'changeLossModal', visible: true }))
        .catch(e => console.log(e))
    }
    this.$store.dispatch('changeModalVisibility', { entityType: 'customerVendorForm', visible: false })
    this.$store.dispatch('changeModalVisibility', { entityType: 'batch', visible: false })
    this.$store.dispatch('isEditingForm', false)
    this.$store.dispatch('clearFilters')
      .then(() => next())
      .catch(e => {
        console.log(e)
        next()
      })
  }
}



// WEBPACK FOOTER //
// ./src/Mixins/SystemViewMixins.js